import { isRecord } from '@fmnts/core';
import * as Arr from 'effect/Array';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import * as P from 'effect/Predicate';
import {
  ConditionBase,
  ConditionBuilder,
  ConditionFn,
} from '../condition.type';

export interface ConditionHasSome extends ConditionBase {
  /** A condition */
  has_some: Record<string, unknown>;
}
export interface ResolvedConditionHasSome {
  has_some: ConditionFn;
}

/**
 * Type guard that checks if an object fulfills the `ConditionHasSome` interface
 *
 * @param c Non nullish object
 * @returns `true` if `c` satisfies `ConditionHasSome`
 */
export function isConditionHasSome(
  c: Record<string, any>,
): c is ConditionHasSome {
  return isRecord(c) && P.hasProperty(c, 'has_some') && isRecord(c['has_some']);
}

const _canBeChecked: P.Refinement<unknown, readonly unknown[]> = P.compose(
  Arr.isArray as P.Refinement<unknown, unknown[]>,
  Arr.isNonEmptyReadonlyArray,
);

/**
 * @param condition
 *
 * @returns
 * Condition function that is called with a non-empty array and
 * that returns `true` if any value fulfills the condition function.
 * Otherwise returns `false`.
 */
export const hasSome: ConditionBuilder<
  ResolvedConditionHasSome,
  ReadonlyArray<unknown>
> = (condition) => {
  const { has_some } = condition;

  return Fn.flow(
    O.liftPredicate(_canBeChecked),
    O.map(Arr.some(has_some)),
    O.getOrElse(Fn.constFalse),
  );
};
